import { store } from '../../redux/store';
import httpService from '../httpService';
import { CommunityInvite } from '../../models/inviteService.model';
import { addInviteFields } from '../../redux/slices/applicationSlice';
import { getSelectedCommunity } from '../../hooks/community/useSelectedCommunity';
import i18n from '../../locales/i18n';
import { RootState } from '../../redux/reducers';

/*
 * Invite service, contains all logic for invite related actions
 */
const inviteService = {
  /**
   * Gets the invite fields from the server or from the store
   * @returns {Promise<CommunityInvite>} - The invite fields
   */
  fetchInvite: async (): Promise<CommunityInvite> => {
    const communityId = store.getState().application.selectedCommunity;
    if (!communityId) {
      throw new Error('NO_COMMUNITY_SELECTED');
    }
    const { data } = await httpService.get<CommunityInvite>(`/networks/communities/${communityId}/invite-link`).catch((err) => {
      return Promise.reject(err);
    });
    store.dispatch(addInviteFields(data));

    return data;
  },

  /**
   * Creates an invite url on the backend
   * @param {string[]} requiredFields - The required fields for the invite
   * @param {string[]} customFields - The custom fields for the invite
   * @returns {Promise<string>} - The invite url
   */
  createInvite: async (invite: CommunityInvite): Promise<CommunityInvite> => {
    const communityId = store.getState().application.selectedCommunity;
    const { data: fields } = await httpService.patch<CommunityInvite>(
      `/networks/communities/${communityId}/invite-link`,
      {
        automaticAccept: invite.acceptAutomatically,
        message: invite.message || i18n.t('invite.default', { name: getSelectedCommunity()?.name ?? '' }),
      }
    );
    store.dispatch(addInviteFields(fields));

    return fields;
  },

  updateInvite: async (invite: CommunityInvite): Promise<CommunityInvite> => {
    const { friendlyId, linkExpiry } = getSelectedCommunity()?.inviteFields || {};
    const communityId = store.getState().application.selectedCommunity;
    const { data: fields } = await httpService.patch<CommunityInvite>(`/networks/communities/${communityId}/invite-link/`, invite);
    store.dispatch(
      addInviteFields({
        ...fields,
        friendlyId: friendlyId || fields.friendlyId,
        linkExpiry,
      }),
    );

    return fields;
  },

  inviteConnections: async (userIds: string[]) => {
    const { selectedCommunity } = (store.getState() as RootState).application;
    return httpService.post(`/networks/communities/${selectedCommunity}/invites`, { othersIds: userIds });
  },

  getNotInvitedConnections: async (userIds: number[]): Promise<number[]> => {
    const { selectedCommunity } = (store.getState() as RootState).application;
    const { data } = await httpService.get<number[]>(
      `/networks/communities/${selectedCommunity}/invites/connections-to-invite`,
      { params: { userIds } }
    );
    return data;
  },
};

export default inviteService;
